import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  BookOpen,
  Clock,
  ArrowRight,
  Moon,
  Heart,
  Brain,
  Lightbulb,
  Users,
  Star,
  Calendar
} from "lucide-react";
import LandingNavigation from "@/components/LandingNavigation";
import Footer from "@/components/ui/footer";
import { useSEO, SEO_CONFIGS } from "@/hooks/useSEO";

const featuredArticles = [
  {
    title: "10 Tips for Creating the Perfect Bedtime Routine",
    excerpt: "Discover science-backed strategies to help your child transition smoothly from playtime to peaceful sleep.",
    category: "Bedtime Tips",
    readTime: "5 min read",
    date: "Dec 15, 2024",
    image: "/blog/bedtime-routine.jpg",
    slug: "perfect-bedtime-routine"
  },
  {
    title: "How AI Storytelling Helps Children Learn",
    excerpt: "Explore the educational benefits of personalized stories and how AI adapts to your child's interests.",
    category: "Education",
    readTime: "7 min read",
    date: "Dec 10, 2024",
    image: "/blog/ai-learning.jpg",
    slug: "ai-storytelling-learning"
  },
  {
    title: "Building Emotional Intelligence Through Stories",
    excerpt: "Learn how bedtime stories can help children understand and express their emotions.",
    category: "Child Development",
    readTime: "6 min read",
    date: "Dec 5, 2024",
    image: "/blog/emotional-intelligence.jpg",
    slug: "emotional-intelligence-stories"
  }
];

const categories = [
  { name: "Bedtime Tips", icon: Moon, count: 12, color: "bg-purple-100 text-purple-700" },
  { name: "Parenting", icon: Heart, count: 8, color: "bg-pink-100 text-pink-700" },
  { name: "Child Development", icon: Brain, count: 10, color: "bg-blue-100 text-blue-700" },
  { name: "Storytelling Ideas", icon: Lightbulb, count: 15, color: "bg-yellow-100 text-yellow-700" },
  { name: "Family Activities", icon: Users, count: 6, color: "bg-green-100 text-green-700" }
];

const recentPosts = [
  {
    title: "Why Personalized Stories Matter for Child Development",
    date: "Dec 12, 2024",
    category: "Education"
  },
  {
    title: "Screen Time Balance: Making Bedtime Tech Work for You",
    date: "Dec 8, 2024",
    category: "Parenting"
  },
  {
    title: "Creating Characters Your Child Will Love",
    date: "Dec 3, 2024",
    category: "Storytelling Ideas"
  },
  {
    title: "The Science Behind Bedtime Rituals",
    date: "Nov 28, 2024",
    category: "Bedtime Tips"
  },
  {
    title: "How to Use Stories to Teach Values",
    date: "Nov 25, 2024",
    category: "Child Development"
  }
];

export default function BlogPage() {
  useSEO(SEO_CONFIGS.blog);

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 via-white to-violet-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <LandingNavigation />

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 mb-6">
            <BookOpen className="h-4 w-4" />
            <span className="text-sm font-medium">SnuggleCrafters Blog</span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Parenting Tips & Bedtime Wisdom
          </h1>

          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8">
            Expert advice on bedtime routines, storytelling tips, and child development insights from the SnuggleCrafters team.
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="pb-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category, index) => (
              <button
                key={index}
                className={`flex items-center gap-2 px-4 py-2 rounded-full ${category.color} hover:opacity-80 transition-opacity`}
              >
                <category.icon className="h-4 w-4" />
                <span className="font-medium">{category.name}</span>
                <span className="text-sm opacity-75">({category.count})</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="py-12 px-4 bg-white dark:bg-gray-800/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold mb-8">Featured Articles</h2>

          <div className="grid md:grid-cols-3 gap-6">
            {featuredArticles.map((article, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow overflow-hidden">
                <div className="aspect-video bg-gradient-to-br from-purple-200 to-pink-200 dark:from-purple-900 dark:to-pink-900 relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <BookOpen className="h-12 w-12 text-purple-500/50" />
                  </div>
                </div>
                <CardHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="secondary" className="text-xs">
                      {article.category}
                    </Badge>
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {article.readTime}
                    </span>
                  </div>
                  <CardTitle className="text-xl line-clamp-2 hover:text-purple-600 cursor-pointer">
                    {article.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-3 mb-4">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {article.date}
                    </span>
                    <Button variant="ghost" size="sm" className="text-purple-600 gap-1 p-0">
                      Read More <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Posts & Sidebar */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <h2 className="text-2xl md:text-3xl font-bold mb-8">Recent Posts</h2>

              <div className="space-y-6">
                {recentPosts.map((post, index) => (
                  <Card key={index} className="border-0 shadow-md hover:shadow-lg transition-shadow">
                    <CardContent className="flex items-center gap-4 py-4">
                      <div className="h-16 w-16 rounded-lg bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/50 dark:to-pink-900/50 flex items-center justify-center flex-shrink-0">
                        <BookOpen className="h-8 w-8 text-purple-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-lg hover:text-purple-600 cursor-pointer line-clamp-1">
                          {post.title}
                        </h3>
                        <div className="flex items-center gap-3 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {post.category}
                          </Badge>
                          <span className="text-sm text-gray-500">{post.date}</span>
                        </div>
                      </div>
                      <ArrowRight className="h-5 w-5 text-gray-400" />
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="mt-8 text-center">
                <Button variant="outline" size="lg" className="gap-2">
                  Load More Articles
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Newsletter */}
              <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-500 to-pink-500 text-white">
                <CardHeader>
                  <CardTitle className="text-xl">Subscribe to Our Newsletter</CardTitle>
                  <CardDescription className="text-white/80">
                    Get the latest parenting tips and storytelling ideas delivered to your inbox.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <input
                      type="email"
                      placeholder="Enter your email"
                      className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/30 text-white placeholder:text-white/60 focus:outline-none focus:ring-2 focus:ring-white/50"
                    />
                    <Button className="w-full bg-white text-purple-600 hover:bg-white/90">
                      Subscribe
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Popular Tags */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg">Popular Topics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {["bedtime routine", "sleep tips", "AI stories", "parenting", "child development", "creativity", "reading", "family time"].map((tag, index) => (
                      <Badge key={index} variant="secondary" className="cursor-pointer hover:bg-purple-100">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* CTA */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Star className="h-5 w-5 text-yellow-500" />
                    Try SnuggleCrafters
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                    Put these tips into practice with personalized AI bedtime stories.
                  </p>
                  <Link href="/auth">
                    <Button className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                      Start Free Trial
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
